import { useEffect, useMemo, useRef, useState } from 'react'
import type { PendingDoc } from '../api'
import { MorphingMark } from './MorphingMark'

// Cheeky, planner-flavoured commentary. Each line gets typed in,
// pauses for a beat, then gets back-spaced before the next one
// arrives — same vibe as Apple Intelligence's prompt animation.
//
// Pool is deliberately large so two consecutive runs don't read the
// same script. On mount we Fisher-Yates the array, so the order on
// every visit is fresh even when the pool itself hasn't changed.
const BASE_MESSAGES: string[] = [
  'Dropping a pin on your block',
  'Geocoding the address',
  'Tracing the cadastre boundary',
  'Measuring frontage (twice, to be sure)',
  'Counting site area to the nearest m²',
  'Checking the lot orientation',
  'Looking up neighbouring titles',
  'Asking the spatial portal nicely',
  'Knocking on the Planning Portal',
  'Pulling the zone map',
  'Reading the LEP land-use table',
  'Confirming the permitted use',
  'Checking heritage overlays',
  'Cross-checking flood and bushfire',
  'Looking for acid sulfate soil',
  'Checking the coastal management line',
  'Hunting for biodiversity overlays',
  'Eyeing the riparian buffer',
  'Reading the LEP — every prescribed clause',
  'Translating LEP language out of legalese',
  'Spotting the FSR cap',
  'Spotting the height cap',
  'Looking for FSR and height bonuses',
  'Reading exceptions to development standards',
  'Walking the DCP — every page, even the appendices',
  'Carving setbacks off the lot',
  'Working out the rear setback',
  'Computing landscaped area minimums',
  'Pencilling in deep-soil zones',
  'Stacking up the 3D envelope',
  'Testing solar access for the neighbours',
  'Visual-checking the streetscape',
  'Checking the parking ratio',
  'Reading the storage and amenity rules',
  'Running the CDC eligibility test',
  'Comparing Council DA against CDC',
  'Counting the dwellings that fit',
  'Sanity-checking the yield',
  'Triple-checking the citations',
  'Lining up source clauses',
  'Polishing your feasibility report',
  'Folding in the council-issued overlays',
  'Final pass: did we miss anything?',
  'Putting the kettle on for the planner',
  'Reading the fine print so you don\'t have to',
  'Doing the maths twice',
  'Checking the answer against another council',
  'Calling the dual-occupancy minimum-lot rule',
  'Pricing the bonuses against the constraints',
  'Sketching what could go on the block',
]

// Extra typewriter lines we splice into the rotation when the planner
// uploaded a cert / contract along with the address. Mentioning the
// docs by name in the typewriter copy keeps the user assured that the
// upload is actively being read — not stuck in a queue somewhere.
const CERT_MESSAGES: string[] = [
  'Opening your Section 10.7 certificate',
  'Reading the prescribed-matter table',
  'Picking out the council\'s overlays',
  'Looking for the explicit CDC line',
  'Cross-checking the council letterhead',
  'Catching the bushfire / flood call',
  'Reading the heritage box',
  'Verifying the issue date',
  'Pulling the council\'s zoning answer',
  'Folding the 10.7 into Section B',
  'Reading the boxed callouts on page 2',
  'Looking for road-widening reservations',
  'Capturing the contamination notice',
  'Hunting for the loose-fill asbestos flag',
]

const CONTRACT_MESSAGES: string[] = [
  'Flipping to the 88B instrument',
  'Catching every easement and covenant',
  'Highlighting special conditions',
  'Reading the registered plan',
  'Identifying the burdened and benefited lots',
  'Measuring the easement strip',
  'Pulling the restrictive covenant text',
  'Checking for a positive covenant',
  'Cross-checking the title detail',
  'Looking for vendor warranties',
  'Reading the demolition clause (if any)',
  'Marking up the encroachments',
  'Folding the 88B into Title encumbrances',
]

const TYPE_MS = 35
const HOLD_MS = 1100
const ERASE_MS = 14

// Tone-of-voice status line shown next to the elapsed counter.
// Buckets escalate honestly — we don't promise 10–20 s anymore, since
// real runs are routinely longer when the LEP / DCP parse caches are
// cold. Each bucket carries a larger pool of variants so even a long
// run doesn't recycle the same handful of lines.
const PATIENCE: Array<{ until: number; lines: readonly string[] }> = [
  {
    until: 5,
    lines: [
      'warming up',
      'just a moment',
      'connecting to the spatial portal',
      'opening the planning tabs',
      'spinning up the analysis',
      'collecting the inputs',
      'getting comfortable',
    ],
  },
  {
    until: 12,
    lines: [
      'thinking carefully',
      'pulling the zone map',
      'checking the cadastre',
      'reading the LEP',
      'measuring twice',
      'looking up your council',
      'checking what\'s permitted on this zone',
      'noting the housing form',
    ],
  },
  {
    until: 22,
    lines: [
      'cross-checking the council data',
      'walking the DCP appendices',
      'looking for prescribed exceptions',
      'matching layers to your lot',
      'lining up the spatial overlays',
      'reading clauses in order',
      'reconciling the LEP and DCP',
      'checking the deep-soil rule',
    ],
  },
  {
    until: 38,
    lines: [
      'still on it — long runs happen',
      'this council has a generous DCP',
      'reading the boxed-callout clauses',
      'second-guessing the FSR bonuses',
      'making sure no exception is missed',
      'reading the schedule for incentives',
      'checking the height-in-storeys cap',
      'verifying the CDC pathway',
    ],
  },
  {
    until: 60,
    lines: [
      'this lot is a chunky read',
      'three layers deep into the controls',
      'making sure no clause gets missed',
      'no shortcuts on a feasibility',
      'a generous appendix — worth reading',
      'cross-referencing two SEPPs',
      'pencilling the building envelope',
      'sanity-checking the dwelling count',
    ],
  },
  {
    until: 110,
    lines: [
      'taking its time to be right',
      'thoroughness over speed',
      'one more pass through the appendices',
      'lining up the citations',
      'reading what the council actually wrote',
      'matching every overlay to a clause',
      'making sure the citations are tight',
      'second-guessing the yield number',
    ],
  },
  {
    until: 9_999,
    lines: [
      'still working — careful is better than fast',
      'a planner would take an afternoon on this',
      'stay with it — the answer is worth the wait',
      'reading the appendices for the third time',
      'no shortcut here',
      'sometimes the council writes a lot',
      'we promise we\'re still working',
    ],
  },
]

function shuffle<T>(input: readonly T[]): T[] {
  // Fisher–Yates — copy the input first so the source pool isn't
  // mutated. Cheap on 50-element arrays.
  const arr = input.slice()
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

export function LoadingProgress({
  pendingDocs = [],
  onCancel,
}: {
  pendingDocs?: PendingDoc[]
  // Optional escape hatch — `cancel` is wired up by the parent when
  // the loading state was started by an automatic session-restore
  // and the planner wants to start fresh instead. Without it, an
  // auto-restored session locked the form button into "Running…"
  // until the underlying fetch eventually returned.
  onCancel?: () => void
}) {
  const [elapsed, setElapsed] = useState(0)
  const [idx, setIdx] = useState(0)
  const [shown, setShown] = useState('')
  // Stable per-mount random offset so the patience-line rotation
  // starts at a different point on every visit — same elapsed bucket
  // doesn't show the same opener twice in a row.
  const patienceOffsetRef = useRef(Math.floor(Math.random() * 1000))

  // Build the typewriter rotation. When docs are in flight, splice
  // doc-specific lines into the sequence so the planner sees their
  // upload referenced by name as the spinner rolls. We shuffle the
  // base pool on mount so consecutive runs read differently.
  const hasCert = pendingDocs.some((d) => d.kind === 'cert')
  const hasContract = pendingDocs.some((d) => d.kind === 'contract')
  const messages = useMemo(() => {
    const base = shuffle(BASE_MESSAGES)
    const extras: string[] = []
    if (hasCert) extras.push(...shuffle(CERT_MESSAGES))
    if (hasContract) extras.push(...shuffle(CONTRACT_MESSAGES))
    if (extras.length === 0) return base
    // Interleave: insert one doc line every 3 base lines so the user
    // gets steady reminders the docs are being read in parallel.
    const out: string[] = []
    let ei = 0
    base.forEach((m, i) => {
      out.push(m)
      if (ei < extras.length && (i + 1) % 3 === 0) {
        out.push(extras[ei++])
      }
    })
    while (ei < extras.length) out.push(extras[ei++])
    return out
  }, [hasCert, hasContract])

  // Elapsed-time counter (cosmetic, 1/4 s tick).
  useEffect(() => {
    const start = Date.now()
    const t = setInterval(() => setElapsed((Date.now() - start) / 1000), 250)
    return () => clearInterval(t)
  }, [])

  // Typewriter — type → hold → erase → advance. Keeps a scheduled
  // timeout so each frame cancels cleanly on unmount / re-render.
  useEffect(() => {
    const target = messages[idx % messages.length]
    let cancelled = false
    let handle: ReturnType<typeof setTimeout> | undefined

    const tick = (mode: 'type' | 'hold' | 'erase', i: number) => {
      if (cancelled) return
      if (mode === 'type') {
        if (i <= target.length) {
          setShown(target.slice(0, i))
          handle = setTimeout(() => tick('type', i + 1), TYPE_MS)
        } else {
          handle = setTimeout(() => tick('erase', target.length), HOLD_MS)
        }
      } else if (mode === 'erase') {
        if (i >= 0) {
          setShown(target.slice(0, i))
          handle = setTimeout(() => tick('erase', i - 1), ERASE_MS)
        } else {
          setIdx((n) => n + 1)
        }
      }
    }
    tick('type', 0)
    return () => {
      cancelled = true
      if (handle) clearTimeout(handle)
    }
  }, [idx, messages])

  function patienceLine(elapsed: number): string {
    const bucket = PATIENCE.find((b) => elapsed < b.until) ?? PATIENCE[PATIENCE.length - 1]
    const lines = bucket.lines
    // Rotate within the bucket every 4 s. The per-mount offset keeps
    // each visit reading a different opener for the same bucket.
    const idx = (Math.floor(elapsed / 4) + patienceOffsetRef.current) % lines.length
    return lines[idx]
  }

  // Parallel-docs line — gives the user a quiet reassurance that
  // their cert / contract is being read at the same time as the
  // feasibility runs.
  const parallelLine = (() => {
    if (pendingDocs.length === 0) return null
    const names = pendingDocs.map((d) => d.kind === 'cert' ? 's10.7' : 'contract')
    if (names.length === 1) {
      return `Your ${names[0]} is being reviewed in parallel — it'll merge into Section B the moment it lands.`
    }
    return `Your ${names.join(' + ')} are being reviewed in parallel — they'll merge into Section B as they land.`
  })()

  return (
    <div className="mt-8 relative overflow-hidden rounded-lg glass">
      {/* Iridescent sweep that drifts across the panel behind the
          copy — slow, low saturation, layered over the glass blur. */}
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div
          className="absolute inset-y-0 w-1/2 brand-gradient-bg blur-3xl opacity-30"
          style={{ animation: 'shimmer-sweep 5.2s ease-in-out infinite' }}
        />
      </div>

      <div className="relative px-8 py-12 flex flex-col items-center text-center gap-5">
        {/* Brand mark — morphs blob → house → pin → tree → chart →
            blob through a metaball goo filter. Anchors the loading
            panel visually and gives the wait a story: organic shape,
            housing, location, growth, yield. */}
        <MorphingMark size={96} />
        {/* Three bobbing dots — sit below the mark to echo the
            "working" beat at a smaller scale. Same gradient tint so
            the panel reads as one connected widget. */}
        <div className="flex items-center gap-2">
          <Dot delay="0s" />
          <Dot delay="0.15s" />
          <Dot delay="0.3s" />
        </div>

        <h2 className="min-h-[3.2rem] font-sans text-2xl font-medium text-massing-ink leading-snug">
          <span>{shown}</span>
          <Caret />
        </h2>

        <p className="brand-eyebrow text-massing-ink-light">
          {elapsed.toFixed(1)}s · {patienceLine(elapsed)}
        </p>

        {parallelLine && (
          <p className="max-w-md text-[12.5px] text-massing-ink-light italic leading-snug">
            {parallelLine}
          </p>
        )}

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="mt-2 text-[11px] tracking-[0.12em] uppercase text-massing-ink-light hover:text-massing-ink underline underline-offset-4 decoration-massing-ink-light/40 hover:decoration-massing-ink"
          >
            Cancel · start fresh
          </button>
        )}
      </div>

      <style>{`
        @keyframes bob {
          0%, 100% { transform: translateY(0); opacity: 0.55; }
          50%      { transform: translateY(-5px); opacity: 1; }
        }
      `}</style>
    </div>
  )
}

function Dot({ delay }: { delay: string }) {
  // Bobbing dots in the brand gradient.
  return (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full brand-gradient-bg shadow-[0_0_12px_rgba(173,140,255,0.6)]"
      style={{ animation: 'bob 1.1s ease-in-out infinite', animationDelay: delay }}
    />
  )
}

function Caret() {
  return (
    <span
      className="inline-block w-[3px] h-[1.05em] align-text-bottom ml-1 brand-gradient-bg rounded-sm"
      style={{ animation: 'caret-blink 1s steps(2, start) infinite' }}
    />
  )
}
